import React, {useState, useEffect} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {useHistory, useParams} from 'react-router-dom';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';

import PrimaryAppBar from './PrimaryAppBar';
import SecondaryAppBar from './SecondayAppBar';
import SnackbarSuccess from './SnackbarSuccess';
import AutoCompleteTags from './AutoCompleteTags';
import Instructions from './Instructions';
import UseReadingExerciseAsDialog from './UseReadingExerciseAsDialog';
import {BluePillButton} from '../../common/components/BluePillButton';
import LessonMenu from './LessonMenu';
import SelectMyExerciseDialog from './SelectMyExerciseDialog';
import VisibilityChip from './VisibilityChip';
import {
  faKeyboard,
  faCheck,
  faFont,
  faBook,
  faEye,
  faTimes,
  faArrowUp,
  faArrowDown,
  faPlus,
  faLineColumns,
  faScalpelLineDashed,
} from '@fortawesome/pro-light-svg-icons';
import {faEllipsisV} from '@fortawesome/pro-regular-svg-icons';

import {fetchLesson, selectLessons, updateLesson, selectExercises} from '../../redux/slices/builder';
import {selectUserGroup} from '../../redux/slices/user';
import {
  LESSON,
  APP_READING,
  APP_WORD_WORK,
  APP_WORD_SORT,
  APP_WORD_DIVISION,
  APP_WRITING,
  APP_ASSESSMENT,
  APP_TITLES,
  FEATURES,
  READING_EXERCISE_GOALS_DISPLAY_NAME,
} from '../../constants';

import styles from '../css/Lesson.module.css';
import baseStyles from '../../css/Base.module.css';

export default function Lesson() {
  const dispatch = useDispatch();
  const history = useHistory();

  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [isLessonMenuOpen, toggleLessonMenu] = useState(false);
  const [isAddExerciseMenuOpen, toggleAddExerciseMenu] = useState(false);
  const [isStepMenuOpen, toggleStepMenu] = useState(false);
  const [isExerciseMenuOpen, toggleExerciseMenu] = useState(false);
  const [activeStepIndex, setActiveStepIndex] = useState(null); // Step that menu was clicked
  const [activeExerciseIndex, setActiveExerciseIndex] = useState(null); // Exercise that menu was clicked
  const [isNewExerciseDialogOpen, toggleNewExerciseDialog] = useState(false);
  const [newExerciseType, setNewExerciseType] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isSnackbarOpen, toggleSnackbar] = useState(false);
  const [isUseReadingExerciseAsDialogOpen, toggleUseReadingExerciseAsDialog] = useState(false);

  // Details
  const [isSaving, toggleIsSaving] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState(null);
  const [visibility, setVisibility] = useState('');
  const [tags, setTags] = useState([]);
  const [instructionsHTML, setInstructionsHTML] = useState(null);
  const [instructionsPlainText, setInstructionsPlainText] = useState(null);
  // Details END

  const userGroup = useSelector(selectUserGroup);
  const lessons = useSelector(selectLessons);
  const exercises = useSelector(selectExercises);

  const {lessonID} = useParams();

  const lesson = lessons.find((lesson) => lesson._id === lessonID);
  const steps = lesson?.steps ?? [];
  const {
    tier: {features},
  } = userGroup;
  const isContentProducerGroup = features.includes(FEATURES.VISIBILITY_PUBLIC);
  const isVisibilityChipVisible = !isContentProducerGroup && lesson?.visibility;
  const visibilityFeatures = features.filter((feature) => feature.startsWith('VISIBILITY_'));

  useEffect(() => {
    dispatch(fetchLesson(lessonID));
  }, [dispatch, lessonID]);

  useEffect(() => {
    if (lesson) {
      setName(lesson.name);
      setDescription(lesson.description);
      setVisibility(lesson.visibility);
      setTags(lesson.tags);
      setInstructionsHTML(lesson?.instructions?.html);
      setInstructionsPlainText(lesson?.instructions?.plainText);
    }
  }, [lesson]);

  function handleChangeTab(event, newTabIndex) {
    setActiveTabIndex(newTabIndex);
  }

  function handleClickLessonMenu(event) {
    setAnchorEl(event.currentTarget);
    toggleLessonMenu(true);
  }

  // On clicking "Add Exercise" button
  function handleClickAddExercise(event, stepIndex) {
    setAnchorEl(event.currentTarget);
    setActiveStepIndex(stepIndex);
    toggleAddExerciseMenu(true);
  }

  // Clicking menu item of menu with different exercises to add
  function handleClickExerciseType(type) {
    toggleAddExerciseMenu(false);
    setNewExerciseType(type);
    toggleNewExerciseDialog(true);
  }

  async function handleSave() {
    toggleIsSaving(true);

    const tagIDs = tags.map((tag) => tag._id);
    await dispatch(updateLesson(lessonID, {name, description, visibility, tags: tagIDs}));

    toggleIsSaving(false);
    toggleSnackbar(true);
  }

  async function addExerciseToLesson(exercise) {
    const {_id: exerciseID} = exercise;
    const stepsClone = JSON.parse(JSON.stringify(steps));
    stepsClone[activeStepIndex].exercises.push(exerciseID);
    await dispatch(updateLesson(lessonID, {steps: stepsClone}));
    toggleSnackbar(true);

    if (newExerciseType === APP_READING) {
      toggleUseReadingExerciseAsDialog(true);
    }
  }

  function handleClickStepMenu(event, stepIndex) {
    setAnchorEl(event.currentTarget);
    setActiveStepIndex(stepIndex);
    toggleStepMenu(true);
  }

  function handleClickExerciseMenu(event, exerciseIndex, stepIndex) {
    setAnchorEl(event.currentTarget);
    setActiveStepIndex(stepIndex);
    setActiveExerciseIndex(exerciseIndex);
    toggleExerciseMenu(true);
  }

  async function handleNewStep() {
    const updatedSteps = JSON.parse(JSON.stringify(steps));

    updatedSteps.push({
      name: `Step ${steps.length + 1}`,
      exercises: [],
      status: 'PENDING',
    });

    await dispatch(updateLesson(lessonID, {steps: updatedSteps}));
    toggleSnackbar(true);
  }

  async function handleDeleteStep() {
    toggleStepMenu(false);
    const updatedSteps = steps
      .filter((step, index) => index !== activeStepIndex) // remove step
      // update step name
      .map((step, index) => ({
        ...step,
        name: `Step ${index + 1}`,
      }));

    // Remove reading exercise goals from lesson config
    const config = JSON.parse(JSON.stringify(lesson?.config ?? {}));
    const readingGoals = config?.readingExerciseGoals;
    if (readingGoals) {
      // Shift all steps above this one down one
      Object.keys(readingGoals).sort().forEach((key) => {
        if (key.startsWith(activeStepIndex)) delete config.readingExerciseGoals[key];
        
        const [keyStep, keyExerciseIndex] = key.split('-').map((keyPart) => parseInt(keyPart, 10));
        if (keyStep > activeStepIndex) {
          const newKey = `${keyStep - 1}-${keyExerciseIndex}`;
          config.readingExerciseGoals[newKey] = config.readingExerciseGoals[key];
          delete config.readingExerciseGoals[key];
        }
      });
    }

    await dispatch(updateLesson(lessonID, {steps: updatedSteps, config}));
    toggleSnackbar(true);
  }

  // Moves step up or down and renames step name based on index
  // direction: -1 to move up, 1 to move down
  async function handleMoveStep(direction) {
    toggleStepMenu(false);
    let updatedSteps = steps.filter((step, index) => index !== activeStepIndex); // remove step
    updatedSteps.splice(activeStepIndex + direction, 0, steps[activeStepIndex]); // insert step at new index

    // update step name
    updatedSteps = updatedSteps.map((step, index) => ({
      ...step,
      name: `Step ${index + 1}`,
    }));

    // Update reading exercise goals
    const config = JSON.parse(JSON.stringify(lesson?.config ?? {}));
    const readingGoals = config?.readingExerciseGoals;
    if (readingGoals) {
      const keysUpdated = [];
      const directionInt = parseInt(direction, 10);
      Object.keys(readingGoals).forEach((key) => {
        if (key.startsWith(activeStepIndex)) {
          const newKeyStep = activeStepIndex + directionInt;
          const newKey = `${newKeyStep}-${key.split('-')[1]}`;
          config.readingExerciseGoals[newKey] = lesson.config.readingExerciseGoals[key];
          keysUpdated.push(newKey);
          if (!keysUpdated.includes(key)) {
            delete config.readingExerciseGoals[key];
          }
        } else if (key.startsWith(activeStepIndex + directionInt)) {
          const keyStep = activeStepIndex + directionInt;
          const newKeyStep = keyStep + (directionInt * -1);
          const newKey = `${newKeyStep}-${key.split('-')[1]}`;
          config.readingExerciseGoals[newKey] = lesson.config.readingExerciseGoals[key];
          keysUpdated.push(newKey);
          if (!keysUpdated.includes(key)) {
            delete config.readingExerciseGoals[key];
          }
        }
      });
    }

    await dispatch(updateLesson(lessonID, {steps: updatedSteps, config}));
    toggleSnackbar(true);
  }

  // Moves exercise up or down
  // direction: -1 to move up, 1 to move down
  async function handleMoveExercise(direction) {
    toggleExerciseMenu(false);
    const stepsClone = JSON.parse(JSON.stringify(steps));
    const step = steps[activeStepIndex];
    const updatedExercises = step.exercises.filter((exercise, index) => index !== activeExerciseIndex); // remove exercise
    updatedExercises.splice(activeExerciseIndex + direction, 0, step.exercises[activeExerciseIndex]); // insert exercise at new index
    stepsClone[activeStepIndex].exercises = updatedExercises;

    // Update reading exercise goals
    const config = JSON.parse(JSON.stringify(lesson?.config ?? {}));
    const readingGoals = config?.readingExerciseGoals;
    if (readingGoals) {
      const keysUpdated = [];
      const directionInt = parseInt(direction, 10);
      Object.keys(readingGoals).forEach((key) => {
        if (key === `${activeStepIndex}-${activeExerciseIndex}`) {
          const newKeyExerciseIndex = activeExerciseIndex + directionInt;
          const newKey = `${activeStepIndex}-${newKeyExerciseIndex}`;
          config.readingExerciseGoals[newKey] = lesson.config.readingExerciseGoals[key];
          keysUpdated.push(newKey);
          if (!keysUpdated.includes(key)) {
            delete config.readingExerciseGoals[key];
          }
        } else if (key === `${activeStepIndex}-${activeExerciseIndex + directionInt}`) {
          const newKeyExerciseIndex = activeExerciseIndex + directionInt + (directionInt * -1);
          const newKey = `${activeStepIndex}-${newKeyExerciseIndex}`;
          config.readingExerciseGoals[newKey] = lesson.config.readingExerciseGoals[key];
          keysUpdated.push(newKey);
          if (!keysUpdated.includes(key)) {
            delete config.readingExerciseGoals[key];
          }
        }
      });
    }

    await dispatch(updateLesson(lessonID, {steps: stepsClone, config}));
    toggleSnackbar(true);
  }

  async function handleRemoveExercise() {
    toggleExerciseMenu(false);
    const stepsClone = JSON.parse(JSON.stringify(steps));
    const step = steps[activeStepIndex];
    const updatedExercises = step.exercises.filter((exercise, index) => index !== activeExerciseIndex); // remove exercise
    stepsClone[activeStepIndex].exercises = updatedExercises;

    // Update reading exercise goals
    const config = JSON.parse(JSON.stringify(lesson?.config ?? {}));
    const readingGoals = config?.readingExerciseGoals;
    if (readingGoals) {
      const activeKey = `${activeStepIndex}-${activeExerciseIndex}`;
      if (readingGoals[activeKey]) delete config.readingExerciseGoals[activeKey];

      // Shift all exercises above this one down one
      Object.keys(readingGoals).sort().forEach((key) => {
        const [keyStep, keyExerciseIndex] = key.split('-').map((keyPart) => parseInt(keyPart, 10));
        if (keyStep === activeStepIndex && keyExerciseIndex > activeExerciseIndex) {
          const newKey = `${keyStep}-${keyExerciseIndex - 1}`;
          config.readingExerciseGoals[newKey] = config.readingExerciseGoals[key];
          delete config.readingExerciseGoals[key];
        }
      });
    }

    await dispatch(updateLesson(lessonID, {steps: stepsClone, config}));
    toggleSnackbar(true);
  }

  function handleClickPreviewExercise() {
    toggleExerciseMenu(false);
    const exerciseID = steps[activeStepIndex].exercises[activeExerciseIndex];
    window.open(`/exercise/${exerciseID}`);
  }

  const exerciseIcon = {
    [APP_ASSESSMENT]: {
      iconComponent: <FontAwesomeIcon icon={faCheck} className="clr-primary" />,
    },
    [APP_READING]: {
      iconComponent: <FontAwesomeIcon icon={faBook} className="clr-primary" />,
    },
    [APP_WORD_DIVISION]: {
      iconComponent: <FontAwesomeIcon icon={faScalpelLineDashed} className="clr-primary" />,
    },
    [APP_WORD_SORT]: {
      iconComponent: <FontAwesomeIcon icon={faLineColumns} className="clr-primary" />,
    },
    [APP_WORD_WORK]: {
      iconComponent: <FontAwesomeIcon icon={faFont} className="clr-primary" />,
    },
    [APP_WRITING]: {
      iconComponent: <FontAwesomeIcon icon={faKeyboard} className="clr-primary" />,
    },
  };

  function renderAddExerciseMenu() {
    return (
      <Menu anchorEl={anchorEl} open={isAddExerciseMenuOpen} onClose={() => toggleAddExerciseMenu(false)} keepMounted>
        {Object.keys(exerciseIcon).map((exerciseType) => {
          const MenuIcon = exerciseIcon[exerciseType].iconComponent;
          return (
            <MenuItem key={APP_TITLES[exerciseType]} onClick={() => handleClickExerciseType(exerciseType)}>
              <ListItemIcon>{MenuIcon}</ListItemIcon>
              <ListItemText primary={APP_TITLES[exerciseType]} />
            </MenuItem>
          );
        })}
      </Menu>
    );
  }

  function renderStepMenu() {
    const isMoveUpEnabled = activeStepIndex > 0;
    const isMoveDownEnabled = activeStepIndex < steps.length - 1;
    return (
      <Menu anchorEl={anchorEl} open={isStepMenuOpen} onClose={() => toggleStepMenu(false)}>
        <MenuItem disabled={!isMoveUpEnabled} onClick={() => handleMoveStep(-1)}>
          <ListItemIcon>
            <FontAwesomeIcon icon={faArrowUp} />
          </ListItemIcon>
          <ListItemText primary="Move Up" />
        </MenuItem>
        <MenuItem disabled={!isMoveDownEnabled} onClick={() => handleMoveStep(1)}>
          <ListItemIcon>
            <FontAwesomeIcon icon={faArrowDown} />
          </ListItemIcon>
          <ListItemText primary="Move Down" />
        </MenuItem>
        <MenuItem onClick={handleDeleteStep}>
          <ListItemIcon>
            <FontAwesomeIcon icon={faTimes} className="clr-danger" />
          </ListItemIcon>
          <ListItemText primary="Remove" className="clr-danger" />
        </MenuItem>
      </Menu>
    );
  }

  function renderExerciseMenu() {
    const isMoveUpEnabled = activeExerciseIndex > 0;
    const isMoveDownEnabled = activeExerciseIndex < steps[activeStepIndex]?.exercises?.length - 1;
    return (
      <Menu anchorEl={anchorEl} open={isExerciseMenuOpen} onClose={() => toggleExerciseMenu(false)}>
        <MenuItem disabled={!isMoveUpEnabled} onClick={() => handleMoveExercise(-1)}>
          <ListItemIcon>
            <FontAwesomeIcon icon={faArrowUp} />
          </ListItemIcon>
          <ListItemText primary="Move Up" />
        </MenuItem>
        <MenuItem disabled={!isMoveDownEnabled} onClick={() => handleMoveExercise(1)}>
          <ListItemIcon>
            <FontAwesomeIcon icon={faArrowDown} />
          </ListItemIcon>
          <ListItemText primary="Move Down" />
        </MenuItem>
        <MenuItem onClick={handleClickPreviewExercise}>
          <ListItemIcon>
            <FontAwesomeIcon icon={faEye} />
          </ListItemIcon>
          <ListItemText primary="Preview" />
        </MenuItem>
        <MenuItem onClick={handleRemoveExercise}>
          <ListItemIcon>
            <FontAwesomeIcon icon={faTimes} className="clr-danger" />
          </ListItemIcon>
          <ListItemText primary="Remove" className="clr-danger" />
        </MenuItem>
      </Menu>
    );
  }

  function renderStep(step, index) {
    return (
      <div key={step.name} className="p-tb-10 p-lr-30">
        <div className={styles['step-row']}>
          <Typography variant="h4">{step.name}</Typography>
          <IconButton onClick={(event) => handleClickStepMenu(event, index)} size="large">
            <FontAwesomeIcon icon={faEllipsisV} size="sm" />
          </IconButton>
        </div>

        {step.exercises.map((exerciseID, exerciseIndex) => {
          const exercise = exercises.find((exercise) => exercise._id === exerciseID);
          return exercise ? renderExerciseCard(exercise, exerciseIndex, index) : null;
        })}

        <div className={[styles.nested, styles['step-row']].join(' ')}>
          <Button
            className={baseStyles['btn-pill']}
            variant="outlined"
            startIcon={<FontAwesomeIcon icon={faPlus} className="clr-gray-400" />}
            onClick={(event) => handleClickAddExercise(event, index)}
          >
            ADD EXERCISE
          </Button>
        </div>
      </div>
    );
  }

  function renderExerciseCard(exercise, index, stepIndex) {
    const MenuIcon = exerciseIcon[exercise.type].iconComponent;

    function getChipLabel() {
      if (exercise.type === APP_READING) {
        const key = `${stepIndex}-${index}`;
        const readingGoal = lesson?.config?.readingExerciseGoals?.[key] ?? null;
        if (readingGoal) return READING_EXERCISE_GOALS_DISPLAY_NAME[readingGoal];
      }
      return APP_TITLES[exercise.type];
    }

    return (
      <Paper key={`${exercise._id}-${stepIndex}-${index}`} className="m-b-10">
        <List dense={false}>
          <ListItem>
            <ListItemAvatar>
              <Avatar className={styles.avatar}>{MenuIcon}</Avatar>
            </ListItemAvatar>
            <ListItemText primary={exercise?.name ?? APP_TITLES[exercise.type]} secondary={exercise?.description} />
            <Chip label={getChipLabel()} size="small" className="clr-primary" />
            <ListItemSecondaryAction>
              <IconButton edge="end" onClick={(event) => handleClickExerciseMenu(event, index, stepIndex)} size="large">
                <FontAwesomeIcon icon={faEllipsisV} size="sm" />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
        </List>
      </Paper>
    );
  }

  function handleNameChanged(event) {
    setName(event.target.value);
  }

  function handleDescriptionChanged(event) {
    setDescription(event.target.value);
  }

  function handleVisibilityChanged(event) {
    setVisibility(event.target.value);
  }

  function handleClickCancel() {
    history.push('/builder/lessons');
  }

  function isSaveButtonEnabled() {
    return !!name && !!visibility && !isSaving;
  }

  function renderTabDetails() {
    return (
      <>
        <div className="p-lr-15 p-b-15">
          <Typography variant="subtitle1" className="clr-dark-blue fnt-bold">
            Basic Info
          </Typography>
          <Grid container>
            <Grid item xl={2} lg={4} md={6} sm={8} xs={12}>
              <TextField
                variant="standard"
                autoFocus
                margin="dense"
                label="Name"
                onChange={handleNameChanged}
                value={name}
                required
                fullWidth
              />
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xl={2} lg={4} md={6} sm={8} xs={12}>
              <TextField
                variant="standard"
                className={baseStyles['m-t-10']}
                value={description ?? ''}
                onChange={handleDescriptionChanged}
                label="Description"
                multiline
                fullWidth
              />
            </Grid>
          </Grid>

          {visibilityFeatures.length > 1 && (
            <div className="m-t-30">
              <Typography variant="subtitle1" className="clr-dark-blue fnt-bold">
                Visibility
              </Typography>
              <Typography variant="body2" className="fnt-bold">
                You may keep this lesson for private use, or share for others to use.
              </Typography>
              <FormControl>
                <RadioGroup value={visibility} onChange={handleVisibilityChanged}>
                  <FormControlLabel value="PRIVATE" control={<Radio />} label="Private" />
                  <FormControlLabel value="USER_GROUP" control={<Radio />} label="Everyone" />
                </RadioGroup>
              </FormControl>
            </div>
          )}

          <Grid container className="m-t-30">
            <Grid item xs={12} md={6} xl={4}>
              <Typography variant="subtitle1" className="clr-dark-blue fnt-bold">
                Tags
              </Typography>
              <Typography variant="body2" className="fnt-bold">
                Create a tag system to organize and find files and other information faster.
              </Typography>
              <AutoCompleteTags tags={tags} setTags={setTags} />
            </Grid>
          </Grid>
        </div>
        <AppBar position="fixed" className="bg-gray-100" sx={{top: 'auto', bottom: 0}}>
          <Toolbar>
            <Grid container direction="row" justifyContent="center" alignItems="center">
              <Button className={baseStyles['btn-pill']} onClick={handleClickCancel}>
                Cancel
              </Button>
              <BluePillButton variant="outlined" disabled={!isSaveButtonEnabled()} onClick={handleSave}>
                {isSaving ? <CircularProgress size={25} /> : 'Save'}
              </BluePillButton>
            </Grid>
          </Toolbar>
        </AppBar>
        <Toolbar /> {/* Makes AppBar at the bottom of the page */}
      </>
    );
  }

  async function handleCloseUseReadingExerciseAsDialog(useReadingExerciseAs) {
    toggleUseReadingExerciseAsDialog(false);

    const stepExercises = steps[activeStepIndex].exercises;
    const index = stepExercises.length - 1;
    const key = `${activeStepIndex}-${index}`;
    let config = JSON.parse(JSON.stringify(lesson?.config ?? {}));
    const readingGoals = config?.readingExerciseGoals;

    if (readingGoals) config.readingExerciseGoals[key] = useReadingExerciseAs;
    else config = {...config, readingExerciseGoals: {[key]: useReadingExerciseAs}};

    await dispatch(updateLesson(lessonID, {config}));
  }

  function renderTabConfigure(steps) {
    return (
      <>
        {renderAddExerciseMenu()}
        {renderStepMenu()}
        {renderExerciseMenu()}

        <SelectMyExerciseDialog
          isOpen={isNewExerciseDialogOpen}
          handleClose={() => toggleNewExerciseDialog(false)}
          exerciseType={newExerciseType}
          onSelectExercise={addExerciseToLesson}
        />

        <UseReadingExerciseAsDialog
          isOpen={isUseReadingExerciseAsDialogOpen}
          onClose={handleCloseUseReadingExerciseAsDialog}
        />

        {steps.length > 0 && steps.map(renderStep)}

        <div className="p-20">
          <div className={styles['new-step-container']}>
            <Button
              className={baseStyles['btn-pill']}
              variant="outlined"
              startIcon={<FontAwesomeIcon icon={faPlus} className="clr-gray-400" />}
              onClick={handleNewStep}
            >
              NEW STEP
            </Button>
          </div>
        </div>
      </>
    );
  }

  async function handleSaveInstructions() {
    await dispatch(updateLesson(lessonID, {instructions: {html: instructionsHTML, plainText: instructionsPlainText}}));
    toggleSnackbar(true);
  }

  return (
    <>
      <LessonMenu anchorEl={anchorEl} isMenuOpen={isLessonMenuOpen} toggleMenu={toggleLessonMenu} lesson={lesson} />
      <SnackbarSuccess text="Lesson Saved." isSnackbarOpen={isSnackbarOpen} toggleSnackbar={toggleSnackbar} />

      <PrimaryAppBar />
      <SecondaryAppBar title={lesson?.name} backURL="/builder/lessons">
        {isVisibilityChipVisible && <VisibilityChip visibility={lesson.visibility} isDarkBackground />}
        <IconButton
          onClick={handleClickLessonMenu}
          size="large"
          className="bg-brandorange m-l-15"
          sx={{width: 45, height: 45, '&:hover': {backgroundColor: 'rgba(238, 65, 39, 0.8)'}}}
        >
          <FontAwesomeIcon icon={faEllipsisV} size="xs" className="clr-gray-200" />
        </IconButton>
      </SecondaryAppBar>

      <Tabs
        className="m-b-30"
        value={activeTabIndex}
        onChange={handleChangeTab}
        indicatorColor="primary"
        textColor="primary"
      >
        <Tab label="DETAILS" />
        <Tab label="CONFIGURE" />
        <Tab label="INSTRUCTIONS" />
      </Tabs>

      {activeTabIndex === 0 && renderTabDetails()}
      {activeTabIndex === 1 && renderTabConfigure(steps)}
      {activeTabIndex === 2 && (
        <Instructions
          instructionsHTML={instructionsHTML}
          setInstructionsHTML={setInstructionsHTML}
          setInstructionsPlainText={setInstructionsPlainText}
          handleClickCancel={handleClickCancel}
          handleSaveInstructions={handleSaveInstructions}
          type={LESSON}
        />
      )}
    </>
  );
}
